import { parseContentDocument, serializeContentDocument, validateContentDocument } from './document.mjs';
import { blockedBookMessage, blockedBookPages, draftBookMessage } from './book-text.mjs';

const MAX_REQUEST_BYTES = 72 * 1024 * 1024;

/**
 * Lectures holding Saju book pages kept out of the outputs — pictures of the sample, manuscripts to be written again, text
 * over its lines (book-text.mjs) — and those with manuscripts not reviewed yet: { issues, warnings }.
 */
function bookTextIssues(lectures, markdownOf) {
  const issues = [], warnings = [];
  for (const lecture of lectures) {
    let content = null;
    try { content = markdownOf(lecture) ? parseContentDocument(markdownOf(lecture)) : null; } catch { content = null; }
    if (!content) continue;
    const pages = blockedBookPages(content), drafts = draftBookMessage(content), title = lecture.title || '제목 없는 강의';
    if (pages.length) issues.push({ message: `${title}: ${blockedBookMessage(pages, '게시할')}` });
    if (drafts) warnings.push({ message: `${title}: ${drafts}` });
  }
  return { issues, warnings };
}

const BASE = '/api/admin/content/learn/course';
const assetPath = id => {
  if (typeof id !== 'string' || !/^local-[a-f0-9]{64}$/.test(id)) throw new Error('이미지 에셋 ID가 올바르지 않습니다.');
  return `/${id}`;
};
const assetSummary = value => {
  if (!value || typeof value !== 'object' || typeof value.id !== 'string' || typeof value.title !== 'string' ||
      typeof value.description !== 'string' || typeof value.subject !== 'string' || typeof value.topic !== 'string' ||
      !Array.isArray(value.concepts) || !Array.isArray(value.recommendedUses) || !Array.isArray(value.avoidUses) ||
      !Array.isArray(value.keywords) || typeof value.mime !== 'string' || !value.dimensions ||
      !Number.isSafeInteger(value.dimensions.width) || !Number.isSafeInteger(value.dimensions.height) ||
      !value.provenance || typeof value.provenance.kind !== 'string' || typeof value.provenance.source !== 'string') {
    throw new Error('이미지 에셋 응답 형식을 확인할 수 없습니다.');
  }
  assetPath(value.id);
  return value;
};
const assetPage = value => {
  if (!value || !Array.isArray(value.assets) || value.assets.some(asset => !asset || typeof asset !== 'object' || Object.hasOwn(asset, 'data')) ||
      (value.nextCursor != null && typeof value.nextCursor !== 'string'))
    throw new Error('이미지 목록 응답 형식을 확인할 수 없습니다.');
  value.assets.forEach(assetSummary);
  // course-server omits null fields: the last page arrives without nextCursor.
  const nextCursor = value.nextCursor ?? null;
  if (nextCursor !== null) assetPath(nextCursor);
  return { ...value, nextCursor };
};
const assetRecord = value => {
  assetSummary(value);
  if (typeof value.data !== 'string' || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value.data))
    throw new Error('이미지 원본 응답 형식을 확인할 수 없습니다.');
  return value;
};
const generatedDocument = result => {
  const validation = validateContentDocument(result.document);
  if (!validation.valid) throw new Error('생성된 문서가 템플릿 형식에 맞지 않습니다. 기존 내용은 유지됩니다.');
  if (serializeContentDocument(parseContentDocument(result.markdown)) !== serializeContentDocument(result.document))
    throw new Error('생성 결과와 저장 본문이 일치하지 않습니다.');
  return result.document;
};
export function createStudioClient({ getAccessToken = () => localStorage.getItem('auth_token'), fetcher = fetch } = {}) {
  async function session() {
    const token = await getAccessToken();
    try {
      const response = await fetcher('/__studio/auth/session', {
        method: 'GET', headers: { 'X-EduSense-Studio': '1' }, credentials: 'same-origin',
        signal: AbortSignal.timeout(10000), redirect: 'error',
      });
      if (response.ok) {
        const value = await response.json();
        if (typeof value?.available === 'boolean' && typeof value.authenticated === 'boolean') {
          return { available: value.available, authenticated: value.authenticated || Boolean(token),
            ...(Number.isFinite(value.expiresAt) ? { expiresAt: value.expiresAt } : {}) };
        }
      }
    } catch { /* Deployed applications use their existing bearer login. */ }
    return { available: false, authenticated: Boolean(token) };
  }
  async function request(path, method = 'GET', body, timeout = 30000, base = BASE) {
    const token = await getAccessToken();
    if (!token && !(await session()).authenticated) throw new Error('관리자 계정으로 로그인하면 AI 생성과 강좌 저장·게시를 사용할 수 있습니다.');
    const encodedBody = body === undefined ? undefined : JSON.stringify(body);
    if (encodedBody !== undefined && new TextEncoder().encode(encodedBody).byteLength > MAX_REQUEST_BYTES) throw new Error('강좌 저장 요청은 72MiB 이하여야 합니다. 이미지를 줄이거나 강좌를 나누어 주세요.');
    let response;
    try {
      response = await fetcher(`${base}${path}`, {
        method, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json', 'X-EduSense-Studio': '1' },
        credentials: 'same-origin',
        ...(encodedBody === undefined ? {} : { body: encodedBody }),
        signal: AbortSignal.timeout(timeout), redirect: 'error',
      });
    } catch {
      throw new Error('서버에 연결하지 못했거나 응답 시간이 초과되었습니다. 작성한 내용은 유지됩니다. 저장·게시 요청은 처리되었을 수 있으므로 강좌 상태를 다시 확인해 주세요.');
    }
    if (response.status === 401) throw new Error('로그인이 만료되었습니다. 다시 로그인해 주세요. 작성한 내용은 유지됩니다.');
    if (response.status === 403) throw new Error('관리자 권한이 필요한 기능입니다.');
    if (response.status === 409) throw new Error(path.startsWith('/assets')
      ? '같은 이미지 ID가 다른 메타데이터로 등록돼 있습니다. 라이브러리에서 해당 이미지를 확인해 주세요.'
      : '서버의 초안이 변경되었습니다. 기기에 저장한 뒤 최신 강좌를 다시 열어 주세요.');
    if (response.status === 429) throw new Error('생성 요청이 많습니다. 잠시 후 다시 시도해 주세요. 작성한 내용은 유지됩니다.');
    if (response.status === 413) throw new Error('강좌 저장 용량을 초과했습니다. 이미지를 줄이거나 강좌를 나누어 주세요. 현재 편집 내용은 유지됩니다.');
    if (response.status === 422) throw new Error('게시 조건을 충족하지 못했습니다. 강좌 검증에서 누락된 내용을 확인해 주세요. 저장한 초안은 유지됩니다.');
    if (response.status === 502 || response.status === 503) throw new Error('콘텐츠 생성 서비스가 응답하지 못했습니다. 잠시 후 다시 시도해 주세요. 작성한 내용은 유지됩니다.');
    if (!response.ok) throw new Error(`요청을 완료하지 못했습니다 (${response.status}). 작성한 내용은 유지됩니다.`);
    try { return await response.json(); }
    catch { throw new Error('서버 응답 형식을 확인할 수 없습니다. 저장·게시 요청은 처리되었을 수 있으므로 강좌 상태를 다시 확인해 주세요.'); }
  }
  const itemPath = (slug) => {
    if (!/^[a-z0-9][a-z0-9-]{0,99}$/.test(slug)) throw new Error('강좌 주소는 영문 소문자, 숫자, 하이픈 1–100자로 입력해 주세요.');
    return `/items/${encodeURIComponent(slug)}`;
  };
  const draft = (value) => {
    if (!value || !Number.isSafeInteger(value.revision) || value.revision < 1 || !Array.isArray(value.document?.sections) ||
        value.document.sections.some(section => !Array.isArray(section?.lectures))) throw new Error('서버 초안 응답 형식을 확인할 수 없습니다.');
    return value;
  };
  const revision = (value) => {
    if (!Number.isSafeInteger(value) || value < 1) throw new Error('최신 강좌 버전을 확인한 뒤 다시 시도해 주세요.');
    return value;
  };
  return {
    session,
    async listAssets({ cursor = null, limit = 100 } = {}) {
      if (cursor !== null) assetPath(cursor);
      if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200) throw new Error('이미지 목록 크기는 1~200개여야 합니다.');
      const params = new URLSearchParams({ limit: String(limit) });
      if (cursor !== null) params.set('cursor', cursor);
      const value = await request(`/assets?${params}`);
      return assetPage(value);
    },
    async getAsset(id) {
      return assetRecord(await request(`/assets${assetPath(id)}`));
    },
    async registerAsset(asset) {
      assetPath(asset?.id);
      return assetSummary(await request('/assets', 'POST', asset));
    },
    async generate(input) {
      return generatedDocument(await request('/generate', 'POST', input, 180000));
    },
    async list(query = '') {
      const values = await request(`/items?${new URLSearchParams({ query })}`);
      if (!Array.isArray(values) || values.some(value => typeof value?.key?.slug !== 'string')) throw new Error('강좌 목록 응답 형식을 확인할 수 없습니다.');
      return values;
    },
    async getDraft(slug) { return draft(await request(`${itemPath(slug)}/draft`)); },
    async open(slug) { return draft(await request(`${itemPath(slug)}/draft/open`, 'POST')); },
    async save(slug, expectedRevision, document) { return draft(await request(`${itemPath(slug)}/draft`, 'PUT', { expectedRevision: revision(expectedRevision), document })); },
    async validate(document) {
      const result = await request('/validate', 'POST', document);
      if (typeof result?.publishable !== 'boolean' || !Array.isArray(result.issues) || result.issues.some(issue => typeof issue?.message !== 'string')) throw new Error('강좌 검증 응답 형식을 확인할 수 없습니다.');
      const book = bookTextIssues((document?.sections ?? []).flatMap(section => section.lectures ?? []), lecture => lecture.markdown);
      return { ...result, ...(book.issues.length ? { publishable: false, issues: [...result.issues, ...book.issues] } : {}), ...(book.warnings.length ? { warnings: book.warnings } : {}) };
    },
    async review(slug, expectedRevision) {
      return draft(await request(`${itemPath(slug)}/review`, 'POST', { expectedRevision: revision(expectedRevision) }));
    },
    async publish(slug, expectedRevision, confirmationKey) {
      if (confirmationKey !== slug) throw new Error('게시할 강좌 주소를 확인해 주세요.');
      return draft(await request(`${itemPath(slug)}/publish`, 'POST', { expectedRevision: revision(expectedRevision), confirmationKey }));
    },
    async getCourse(courseId, { preview = false } = {}) {
      if (typeof courseId !== 'string' || !/^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(courseId)) throw new Error('강좌 ID를 확인할 수 없습니다.');
      return request(`/${courseId}?preview=${preview === true}`, 'GET', undefined, 30000, '/api/courses');
    },
  };
}

/** Replace one lecture only; retain every unrelated field, section and lecture. */
export function updateCourseLecture(course, lectureId, content) {
  const next = structuredClone(course);
  const lectures = next.sections.flatMap((section) => section.lectures);
  const lecture = lectures.find((entry) => entry.id === lectureId);
  if (!lecture) throw new Error('강좌에서 선택한 강의를 찾을 수 없습니다.');
  lecture.title = content.title;
  lecture.markdown = serializeContentDocument(content);
  return next;
}

const TEMPLATES = '/api/studio/templates';
const TEMPLATE_ID = /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/;

/** Ordinary creator drafts are owned by the signed-in user, independently of the admin CMS. */
export function createCreatorClient({ getAccessToken = () => localStorage.getItem('auth_token'), fetcher = fetch } = {}) {
  const base = '/api/courses/drafts';
  const draftPath = id => {
    if (typeof id !== 'string' || !/^(?:[a-f0-9]{24}|[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12})$/i.test(id))
      throw new Error('강좌 초안 ID가 올바르지 않습니다.');
    return `/${encodeURIComponent(id)}`;
  };
  const checkedDraft = value => {
    if (!value || typeof value.id !== 'string' || !value.id || typeof value.title !== 'string' ||
      typeof value.updatedAt !== 'string' || !Array.isArray(value.sections ?? []) ||
      (value.sections ?? []).some(section => !Array.isArray(section?.lectures ?? [])))
      throw new Error('서버 초안 응답 형식을 확인할 수 없습니다.');
    draftPath(value.id);
    return value;
  };
  async function session() {
    const token = await getAccessToken();
    try {
      const response = await fetcher('/__studio/auth/session', { method: 'GET', headers: { 'X-EduSense-Studio': '1' },
        credentials: 'same-origin', signal: AbortSignal.timeout(10000), redirect: 'error' });
      if (response.ok) {
        const value = await response.json();
        if (typeof value?.available === 'boolean' && typeof value.authenticated === 'boolean')
          return { available: value.available, authenticated: value.authenticated || Boolean(token),
            ...(Number.isFinite(value.expiresAt) ? { expiresAt: value.expiresAt } : {}) };
      }
    } catch { /* Deployed applications use their bearer session. */ }
    return { available: false, authenticated: Boolean(token) };
  }
  async function request(path, method = 'GET', data, timeout = 30000, endpoint = base, json = false) {
    const token = await getAccessToken();
    if (!token && !(await session()).authenticated) throw new Error('일반 사용자 계정으로 로그인하면 강좌를 서버에 저장하고 게시할 수 있습니다.');
    const encoded = data === undefined ? undefined : JSON.stringify(data);
    if (encoded !== undefined && new TextEncoder().encode(encoded).byteLength > MAX_REQUEST_BYTES)
      throw new Error('강좌 저장 요청은 72MiB 이하여야 합니다. 이미지를 줄이거나 강좌를 나누어 주세요.');
    const body = encoded === undefined ? undefined : json ? encoded : new FormData();
    if (body instanceof FormData) body.append('data', encoded);
    let response;
    try {
      response = await fetcher(`${endpoint}${path}`, { method, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-EduSense-Studio': '1', ...(json ? { 'Content-Type': 'application/json' } : {}) }, credentials: 'same-origin', ...(body ? { body } : {}),
        signal: AbortSignal.timeout(timeout), redirect: 'error' });
    } catch {
      throw new Error('서버에 연결하지 못했습니다. 현재 편집 내용은 유지됩니다. 저장·게시 상태를 새로고침해 확인해 주세요.');
    }
    if (response.status === 401) throw new Error('로그인이 만료되었습니다. 다시 로그인해 주세요. 편집 내용은 유지됩니다.');
    if (endpoint === TEMPLATES && !response.ok && response.status !== 401) {
      const [code, message] = ({
        403: ['READ_ONLY', '내가 저장한 템플릿만 삭제할 수 있습니다.'],
        404: ['NOT_FOUND', '템플릿을 찾지 못했습니다. 템플릿 목록을 새로고침해 주세요.'],
        409: ['CONFLICT', '같은 ID의 다른 템플릿이 있습니다. 다시 저장해 주세요.'],
        // The server holds at most 16 MiB, but in production the gateway in front of it refuses a smaller body first.
        413: ['TOO_LARGE', '서버 템플릿으로 저장하기에는 너무 큽니다. 이 기기에 저장해 주세요. 편집 내용은 유지됩니다.'],
        400: ['INVALID_INPUT', '이 콘텐츠는 서버 템플릿으로 저장할 수 없습니다. 이름과 문서 내용을 확인해 주세요.'],
      })[response.status] ?? ['SERVER', `템플릿 요청을 완료하지 못했습니다 (${response.status}).`];
      throw Object.assign(new Error(message), { code });
    }
    if (response.status === 403) throw new Error('이 강좌를 열거나 수정할 권한이 없습니다. 내 강좌를 선택했는지 확인해 주세요.');
    if (response.status === 404) throw new Error('강좌 초안을 찾지 못했습니다. 내 강좌 목록을 새로고침해 주세요.');
    if (response.status === 409) throw new Error(endpoint === '/api/studio/assets'
      ? '같은 이미지 ID가 다른 메타데이터로 등록돼 있습니다. 라이브러리에서 해당 이미지를 확인해 주세요.'
      : '서버의 초안이 변경되었거나 이미 게시되었습니다. 현재 편집 내용을 기기에 저장한 뒤 서버 상태를 확인해 주세요.');
    if (response.status === 413) throw new Error(await tooLargeMessage(response));
    if (response.status === 429) throw new Error('AI 생성 요청이 많습니다. 잠시 후 다시 시도해 주세요. 편집 내용은 유지됩니다.');
    if (response.status === 502 || response.status === 503) throw new Error('콘텐츠 생성 서비스가 응답하지 못했습니다. 잠시 후 다시 시도해 주세요. 편집 내용은 유지됩니다.');
    if (response.status === 400 || response.status === 422) throw new Error('강좌 정보 또는 게시 조건을 확인해 주세요. 편집 내용은 유지됩니다.');
    if (!response.ok) throw new Error(`강좌 요청을 완료하지 못했습니다 (${response.status}). 편집 내용은 유지됩니다.`);
    if (response.status === 204) return null;
    try { return await response.json(); }
    catch { throw new Error('서버 응답을 확인할 수 없습니다. 저장·게시 상태를 새로고침해 확인해 주세요.'); }
  }
  const templatePath = id => {
    if (typeof id !== 'string' || !TEMPLATE_ID.test(id)) throw Object.assign(new Error('템플릿 ID가 올바르지 않습니다.'), { code: 'NOT_FOUND' });
    return `/${id}`;
  };
  const templateSummary = value => {
    if (!value || typeof value.id !== 'string' || !TEMPLATE_ID.test(value.id) || typeof value.title !== 'string' ||
      !['private', 'shared'].includes(value.visibility) || typeof value.mine !== 'boolean' || typeof value.updatedAt !== 'string')
      throw new Error('서버 템플릿 응답 형식을 확인할 수 없습니다.');
    return value;
  };
  // The account template library, in the remote-store shape of template-library.mjs.
  const templates = {
    async available() { return Boolean(await getAccessToken()) || (await session()).authenticated; },
    async list() {
      const rows = [];
      let cursor = null;
      do {
        const params = new URLSearchParams({ limit: '200' });
        if (cursor) params.set('cursor', cursor);
        const value = await request(`?${params}`, 'GET', undefined, 30000, TEMPLATES);
        // course-server omits null fields, so the last page has no nextCursor at all.
        if (!value || !Array.isArray(value.templates) || (value.nextCursor != null && typeof value.nextCursor !== 'string'))
          throw new Error('서버 템플릿 목록 형식을 확인할 수 없습니다.');
        rows.push(...value.templates.map(templateSummary));
        cursor = value.nextCursor ?? null;
      } while (cursor && rows.length < 2000);
      return rows;
    },
    async get(id) {
      const value = templateSummary(await request(templatePath(id), 'GET', undefined, 30000, TEMPLATES));
      if (typeof value.markdown !== 'string') throw new Error('서버 템플릿 본문을 확인할 수 없습니다.');
      return value;
    },
    async add(record) { return templateSummary(await request('', 'POST', record, 60000, TEMPLATES, true)); },
    async update(id, { visibility }) {
      return templateSummary(await request(templatePath(id), 'PATCH', { visibility }, 30000, TEMPLATES, true));
    },
    async remove(id) { await request(templatePath(id), 'DELETE', undefined, 30000, TEMPLATES); },
  };
  return {
    session,
    templates,
    sharedAssetsAvailable: true,
    async listAssets({ cursor = null, limit = 100 } = {}) {
      if (cursor !== null) assetPath(cursor);
      if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200) throw new Error('이미지 목록 크기는 1~200개여야 합니다.');
      const params = new URLSearchParams({ limit: String(limit) });
      if (cursor !== null) params.set('cursor', cursor);
      return assetPage(await request(`?${params}`, 'GET', undefined, 30000, '/api/studio/assets'));
    },
    async getAsset(id) {
      return assetRecord(await request(assetPath(id), 'GET', undefined, 30000, '/api/studio/assets'));
    },
    async registerAsset(asset) {
      assetPath(asset?.id);
      return assetSummary(await request('', 'POST', asset, 30000, '/api/studio/assets', true));
    },
    async generate(input) {
      return generatedDocument(await request('', 'POST', input, 180000, '/api/studio/generate', true));
    },
    async list() {
      const value = await request('?filter=my_drafts&size=100');
      if (!value || !Array.isArray(value.content) || value.content.some(item => !item || typeof item.id !== 'string' || typeof item.title !== 'string'))
        throw new Error('내 강좌 목록 응답 형식을 확인할 수 없습니다.');
      return value.content;
    },
    async getDraft(id) { return checkedDraft(await request(draftPath(id))); },
    async create(document) {
      const title = document?.title?.trim();
      if (!title) throw new Error('강좌 제목을 입력해 주세요.');
      const created = checkedDraft(await request('', 'POST', { title, description: '', sections: [{ id: crypto.randomUUID(), title: '학습 자료', order: 1,
        lectures: [{ id: crypto.randomUUID(), title, order: 1, contentType: 'MARKDOWN', markdownContent: serializeContentDocument(document) }] }] }));
      return checkedDraft(await request(draftPath(created.id)));
    },
    async save(current, next) {
      checkedDraft(current);
      const latest = await request(draftPath(current.id));
      checkedDraft(latest);
      if (latest.updatedAt !== current.updatedAt) throw new Error('서버 초안이 다른 곳에서 변경되었습니다. 현재 편집 내용을 기기에 저장한 뒤 강좌를 다시 열어 주세요.');
      checkedDraft(await request(draftPath(current.id), 'PUT', { title: next.title, description: next.description,
        sections: next.sections }));
      return checkedDraft(await request(draftPath(current.id)));
    },
    async publish(id) {
      const result = await request(`${draftPath(id)}/publish`, 'POST');
      if (typeof result?.courseId !== 'string' || !result.courseId) throw new Error('게시 응답에서 강좌 ID를 확인할 수 없습니다. 서버 상태를 새로고침해 주세요.');
      return result;
    },
    validate(course) {
      const issues = [];
      if (!course?.title?.trim()) issues.push({ message: '강좌 제목을 입력해 주세요.' });
      const lectures = (course?.sections ?? []).flatMap(section => section.lectures ?? []);
      if (!lectures.length) issues.push({ message: '학습 내용을 한 개 이상 추가해 주세요.' });
      for (const lecture of lectures) if (lecture.contentType === 'MARKDOWN' && !lecture.markdownContent?.trim())
        issues.push({ message: `${lecture.title || '제목 없는 강의'}의 본문을 입력해 주세요.` });
      // Saju book pages kept out of the outputs, and manuscripts not reviewed yet (book-text.mjs).
      const book = bookTextIssues(lectures, lecture => lecture.contentType === 'MARKDOWN' ? lecture.markdownContent : null);
      issues.push(...book.issues);
      return { publishable: issues.length === 0, issues, ...(book.warnings.length ? { warnings: book.warnings } : {}) };
    },
  };
}

/** Update only the selected lecture while retaining the creator draft's other content. */
/**
 * Why a course was refused as too large: the server's own words when it measured the document it would store
 * (COURSE_DOCUMENT_TOO_LARGE — MongoDB's limit, with the sizes), otherwise the gateway in front of it refused the body.
 */
async function tooLargeMessage(response) {
  let refusal = null;
  if ((response.headers.get('Content-Type') || '').startsWith('application/json')) {
    try { refusal = await response.json(); } catch { refusal = null; }
  }
  if (refusal?.error === 'COURSE_DOCUMENT_TOO_LARGE' && typeof refusal.message === 'string' && refusal.message.trim())
    return `${refusal.message.trim()} 편집 내용은 유지됩니다.`;
  return '강좌 저장 용량을 초과했습니다. 이미지를 줄여 주세요. 편집 내용은 유지됩니다.';
}

export function updateCreatorLecture(course, lectureId, content) {
  const next = structuredClone(course);
  const lecture = (next.sections ?? []).flatMap(section => section.lectures ?? []).find(item => item.id === lectureId);
  if (!lecture) throw new Error('강좌에서 선택한 강의를 찾을 수 없습니다.');
  lecture.title = content.title;
  lecture.markdownContent = serializeContentDocument(content);
  lecture.contentType = 'MARKDOWN';
  return next;
}
